import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const lastUpdated = 'January 2026';

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="privacy-policy-page">
      {/* Header */}
      <section className="py-12 sm:py-16 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white uppercase mb-4">
              Privacy <span className="text-amber-500">Policy</span>
            </h1>
            <p className="text-slate-300">
              Last updated: {lastUpdated}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 sm:py-20 bg-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="max-w-none">
            <div className="bg-slate-900 border border-slate-800 p-6 sm:p-8 mb-8">
              <p className="text-slate-300 leading-relaxed">
                Pro Painting ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you visit our website or use our services.
              </p>
            </div>

            <div className="space-y-8">
              {/* Information We Collect */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Information We Collect
                </h2>
                <p className="text-slate-300 mb-4">
                  We collect information you provide directly to us, including:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Name:</strong> First and last name</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Contact Information:</strong> Email address and phone number</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Address:</strong> Property address for service estimates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Project Details:</strong> Information about your painting project</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Communication Preferences:</strong> Your consent choices for SMS and email communications</span>
                  </li>
                </ul>
              </div>

              {/* How We Use Your Information */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  How We Use Your Information
                </h2>
                <p className="text-slate-300 mb-4">
                  We use the information we collect to:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Provide quotes and estimates for painting services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Schedule consultations and appointments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Send service-related communications (appointment reminders, project updates)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Send marketing communications (only if you have opted in)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Respond to your inquiries and provide customer support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Improve our website and services</span>
                  </li>
                </ul>
              </div>

              {/* SMS/Text Messaging */}
              <div className="bg-slate-900 border border-amber-500/30 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  SMS/Text Messaging Policy
                </h2>
                <p className="text-slate-300 mb-4">
                  Pro Painting collects phone numbers for the purpose of sending appointment confirmations, reminders, estimates, scheduling updates, and service notifications via SMS text messages. If you opt in to receive SMS text messages from Pro Painting, please note:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Message frequency may vary</strong> based on your interactions and project status</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Message and data rates may apply</strong> depending on your mobile carrier plan</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Reply STOP to opt out</strong> of text messages at any time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Reply HELP for assistance</strong> or contact us at (830) 327-0136</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-500">•</span>
                    <span><strong className="text-white">Consent is not a condition of purchase</strong></span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-slate-800">
                  <h3 className="font-bold text-white mb-3">SMS Data Protection</h3>
                  <ul className="space-y-2 text-slate-300">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>Your phone number and SMS data is <strong className="text-white">not sold or shared with third parties</strong> for marketing or promotional purposes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>SMS information is used solely for appointment and service-related communications</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>We implement appropriate security measures to protect your mobile information</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>You may opt out at any time by replying STOP to any message</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Data Sharing */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Information Sharing and Disclosure
                </h2>
                <p className="text-slate-300 mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share 
                  your information only in the following circumstances:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Service Providers:</strong> With trusted third-party service providers who assist us in operating our business (payment processing, appointment scheduling)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</span>
                  </li>
                </ul>
              </div>

              {/* Data Security */}
              <div className="bg-slate-900 border-2 border-amber-500/50 p-6">
                <h2 className="font-heading font-bold text-2xl text-amber-500 uppercase mb-4">
                  Data Security and Handling
                </h2>
                <p className="text-slate-300 mb-4">
                  <strong className="text-white">Pro Painting</strong> is committed to protecting your personal information. We implement appropriate technical and organizational security measures to safeguard your data:
                </p>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Encryption:</strong> All data transmitted through our website is encrypted using SSL/TLS technology</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Secure Storage:</strong> Personal data is stored on secure servers with restricted access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Access Controls:</strong> Personal information is only accessible to authorized personnel who need it to perform their duties</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Data Retention:</strong> We retain your information only as long as necessary to fulfill the purposes outlined in this policy, after which it is securely deleted</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Third-Party Security:</strong> We carefully vet third-party service providers to ensure they maintain appropriate security standards</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Breach Notification:</strong> In the event of a data breach affecting your personal information, we will notify you as required by applicable law</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span><strong className="text-white">Regular Audits:</strong> We periodically review our security practices to ensure ongoing protection of your data</span>
                  </li>
                </ul>
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <h3 className="font-bold text-white mb-3">How We Handle Your Data</h3>
                  <ul className="space-y-2 text-slate-300 text-sm">
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>Your data is processed only for the purposes stated in this policy</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>We do not sell your personal information to any third party</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>Data is only shared with service providers necessary to deliver our services</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-cyan-500">•</span>
                      <span>You can request deletion of your data at any time by contacting us</span>
                    </li>
                  </ul>
                </div>
                <p className="text-slate-400 mt-4 text-sm">
                  While we implement industry-standard security measures, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your information to the best of our ability.
                </p>
              </div>

              {/* Your Rights */}
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Your Rights
                </h2>
                <p className="text-slate-300 mb-4">
                  You have the right to:
                </p>
                <ul className="space-y-2 text-slate-300">
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Access, update, or delete your personal information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Opt out of marketing communications at any time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500">•</span>
                    <span>Withdraw consent for SMS messaging by replying STOP</span>
                  </li>
                </ul>
              </div>

              {/* Contact Information */}
              <div className="bg-slate-900 border border-slate-800 p-6">
                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                  Contact Us
                </h2>
                <p className="text-slate-300 mb-4">
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 text-slate-400">
                    <Mail className="w-5 h-5 text-amber-500" />
                    <a href="mailto:matthew@propainting.cloud" className="hover:text-amber-500 transition-colors">
                      matthew@propainting.cloud
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400">
                    <Phone className="w-5 h-5 text-amber-500" />
                    <a href="tel:+18303270136" className="hover:text-amber-500 transition-colors">
                      (830) 327-0136
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-slate-400">
                    <MapPin className="w-5 h-5 text-amber-500" />
                    <span>493 N Market St, New Braunfels, TX 78130</span>
                  </div>
                </div>
              </div>

              {/* Related Links */}
              <div className="flex flex-wrap gap-4 pt-6 border-t border-slate-800">
                <Link 
                  to="/terms" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Terms of Service
                </Link>
                <Link 
                  to="/sms-terms" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  SMS Terms
                </Link>
                <Link 
                  to="/contact" 
                  className="text-amber-500 hover:text-amber-400 transition-colors"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
